import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { SignalsService } from './signals.service';
import { KpiService } from './kpi.service';

import domtoimage from 'dom-to-image';  
import jsPDF from 'jspdf';
import { map, Observable, Subject } from 'rxjs';
import { Dashboard, DashboardConfig, dashboardDataType, Scope } from '../models/dashboard-models';
import { DASHBOARD_CONFIG, VEHICLES_OF_CUSTOMER_ENDPOINT } from 'src/app/core/config/config.constants';
import { environment } from 'src/environments/environment';
import { DateRange, KpiWidgetPrebuiltType, SignalsApiRequest, Widget, WidgetType } from '../models/widget-models';
import { Action } from '@ngrx/store';
import { getCompositeSignalDataAction, getKpiDataAction, getMachineDataAction, getSignalsCatalogAction, getSignalsTrendDataAction } from 'src/app/core/store/actions';

@Injectable({
  providedIn: 'root'
})
export class DashboardsService {  
  
  dashboardToDuplicate$: Subject<DashboardConfig> = new Subject<DashboardConfig>();
  updateDashboardsMenu$: EventEmitter<{dashboard: DashboardConfig, action: 'new' | 'edit' | 'duplicate' | 'delete'}> 
    = new EventEmitter<{dashboard: DashboardConfig, action: 'new' | 'edit' | 'duplicate' | 'delete'}>();
  dashboardMenuUpdated$: EventEmitter<null> = new EventEmitter<null>();
  
  private baseEndpoint = DASHBOARD_CONFIG;
  private baseUrl: string = environment.baseTsOhwUrl;
  private baseApi: string = environment.baseApi;

  constructor(private _signalsService: SignalsService,
    private _kpiService: KpiService,
    private _httpClient: HttpClient) {}

  getDashboardsConfig(uidCustomer: string, userId: string): Observable<DashboardConfig[]> {
    const queryString = `?scope=${Scope.DASHBOARD}&uidUser=${userId}&uidCustomer=${uidCustomer}&includeAncestorPath=true`;
    return this._httpClient.get(this.baseUrl + this.baseEndpoint + queryString).pipe(
      map((res: any) => {
        let data: DashboardConfig[] = res.data;
        let dashboards: DashboardConfig[] = res.data || [];
        // Uncomment when published feature is implemented
        /* data.map(ds => {  
          if (ds.config.state === DashboardState.PUBLISHED) {
            switch (ds.config.type) {
              case DashboardType.CUSTOMER:
                dashboards.push(ds);
                break;
              case DashboardType.SELF:
                if (ds.uidUser === this.currentUserInfo.user?.uid) {
                  dashboards.push(ds);
                }
                break;
            }
          }
        }); */
        return dashboards;
      })
    );
  }

  getDashboardById(dashboardId: string): Observable<any> {
    const queryString = `/${dashboardId}`;
    return this._httpClient.get(this.baseUrl + this.baseEndpoint + queryString);
  }

  createDashboard(dashboard: any) {
    return this._httpClient.post(this.baseUrl + this.baseEndpoint, dashboard);
  }

  editDashboard(dashboard: any) {
    return this._httpClient.put(this.baseUrl + this.baseEndpoint, dashboard);
  }

  deleteDashboard(dashboardId: string) {
    return this._httpClient.delete(this.baseUrl + this.baseEndpoint + `?id=${dashboardId}`);
  }

  getDashboardsConfigAsAdmin(command: any): Observable<any> {
    const queryString = `?uidCustomer=${command.uidCustomer}&page=${command.page}&size=${command.size}${command.uidCustomerFilter ? '&uidCustomerFilter=' + command.uidCustomerFilter : ''}`;
    return this._httpClient.get(this.baseUrl + 'v1/rt/admin/config/dashboard/all' + queryString);
  }

  getBrandsOfCustomer(uidCustomer: string): Observable<any> {
    return this._httpClient.get(this.baseApi + VEHICLES_OF_CUSTOMER_ENDPOINT + uidCustomer + '/brands');
  }

  getVehicleOfCustomerById(uidCustomer: string, vehicleId: string): Observable<any> {
    return this._httpClient.get(this.baseApi + VEHICLES_OF_CUSTOMER_ENDPOINT + uidCustomer + '/vehicle/' + vehicleId);
  }

  getActionsToCall(widgets: Widget[], baseCommand: SignalsApiRequest, dashboardType: dashboardDataType, isViewMode: boolean = false): Array<Action> {
    let actions: Array<Action> = [];
    
    const uniqueSignalTypes = this._signalsService.getUniqueSignals(
      widgets.filter(
        w => w.widgetType === WidgetType.SIGNAL
      )
    );
    const uniqueKpiTypes = this._kpiService.getUniqueKpis(
      widgets.filter(
        w => (w.widgetType === WidgetType.KPI || w.widgetType === WidgetType.PIE)
      )
    );
    const isCompositeSignalsIncluded = widgets.some(w => w.widgetType === WidgetType.SIGNAL && w.isCompositeWidget);
    const isVehicleStatsIncluded = widgets.some(w => w.widgetTypeCategory === KpiWidgetPrebuiltType.VEHICLES_RECAP);
    
    const signalsData: SignalsApiRequest = {
      ...baseCommand,
      signals: uniqueSignalTypes,
    };
    const kpiData: SignalsApiRequest = {
      ...baseCommand,
      signals: uniqueKpiTypes,
    };

    if (uniqueSignalTypes.length > 0) {
      actions.push(getSignalsTrendDataAction({ data: signalsData }));
    }
    if (uniqueKpiTypes.length > 0 || isVehicleStatsIncluded) {
      actions.push(getKpiDataAction({ 
        data: kpiData, 
        isVehicleStatsIncluded: isVehicleStatsIncluded ?? false 
      }));
    }

    if (isCompositeSignalsIncluded) {
      const compositeData: SignalsApiRequest = {
        ...baseCommand,
        signals: this._signalsService.getUniqueSignals(
          widgets.filter(
            w => w.widgetType === WidgetType.SIGNAL && w.isCompositeWidget
          ),
          true
        ),
      }
      actions.push(getCompositeSignalDataAction({ data: compositeData }));
    }

    if (dashboardType === 'machine' && baseCommand.machineIds?.length === 1) {
      actions.push(getMachineDataAction({ data: baseCommand.machineIds }));
    }

    // Always add signals available action for edit mode to have the latest signals catalog
    if (!isViewMode)
      actions.push(getSignalsCatalogAction({ data: baseCommand }));

    return actions;
  }

  printDashboard(dashboard: HTMLElement, name: string): void {
    /* Alternative approach to clone the dashboard element to avoid the issue with the original element
     * resetting the top style property
    */
    // Clone the dashboard element
    //const dashboardClone = dashboard.cloneNode(true) as HTMLElement;

    // Copy canvas content from original to clone
    /* const originalCanvases = dashboard.querySelectorAll('canvas');
    const clonedCanvases = dashboardClone.querySelectorAll('canvas');
    originalCanvases.forEach((origCanvas, i) => {
      const cloneCanvas = clonedCanvases[i];
      if (cloneCanvas) {
        const ctx = cloneCanvas.getContext('2d');
        ctx?.drawImage(origCanvas, 0, 0);
      }
    }); */

    let originalTop = dashboard.style.top;
    dashboard.style.top = '0';

    // Append the clone to the body (so it can be rendered)
    //document.body.appendChild(dashboardClone);

    const dashboardHeight = dashboard.clientHeight;
    const dashboardWidth = dashboard.clientWidth;
    const options = {
      background: 'white',
      width: dashboardWidth,
      height: dashboardHeight,
    };
    domtoimage.toPng(dashboard, options).then((imgData: any) => {
      const doc = new jsPDF(
          dashboardWidth > dashboardHeight ? 'l' : 'p',
          'mm',
          [dashboardWidth, dashboardHeight]
      );
      const imgProps = doc.getImageProperties(imgData);
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      doc.save(name);
      
      dashboard.style.top = originalTop;

      // Remove the clone from the DOM
      // document.body.removeChild(dashboardClone);
    });
  }

  getFromDateFilter(dashboard: Dashboard | null): { fromDtPicker: Date | null, timespan: string | null } {
    let baseDate = new Date();
    baseDate.setHours(24, 0, 0, 0);
    let fromDtPicker: Date | null = null;
    let timespan: string | null = null;
    switch (dashboard?.settings.defaultFilterValue) {
      case DateRange.TwentyFourHours:
        fromDtPicker = new Date(baseDate.setDate(baseDate.getDate() - 1));
        timespan = 'daily';
        break;
      case DateRange.SevenDays:
        fromDtPicker = new Date(baseDate.setDate(baseDate.getDate() - 7));
        timespan = 'last7days';
        break;
      case DateRange.FourteenDays:
        fromDtPicker = new Date(baseDate.setDate(baseDate.getDate() - 14));
        timespan = 'timeperiod';
        break;
      default:
        fromDtPicker = null // new Date(baseDate.setDate(baseDate.getDate() - 30));
        timespan = 'month';
        break;
    }
    return { fromDtPicker, timespan };
  }

}